import { DisabledOverlay, SimpleTooltip, TooltipTitleAndMessage, ToolTipWrapper } from "@iventis/components";
import { TooltipProps } from "@mui/material";
import { InteractiveElement, styled } from "@iventis/styles";
import React, { PropsWithChildren, MouseEvent, ReactNode, forwardRef } from "react";

type SidebarTopNavProps = {
    toolTip: string | ReactNode;
    toolTipPlacement?: TooltipProps["placement"];
    toolTipPermission?: string | ReactNode;
    canUserEdit?: boolean;
    disable?: boolean;
    onClick?: (e: MouseEvent) => void;
    testId?: string;
    className?: string;
    disabledTestId?: string;
};

/** Icon button used across the sidebar, handles tooltip being available on disabled buttons and change opacity of disabled buttons */
export const SidebarButton = forwardRef<HTMLButtonElement, PropsWithChildren<SidebarTopNavProps>>(
    ({ toolTip, toolTipPlacement = "bottom", toolTipPermission, canUserEdit = true, disable = false, onClick, testId, disabledTestId, className, children }, ref) => (
        <SimpleTooltip text={canUserEdit ? toolTip : toolTipPermission} placement={toolTipPlacement}>
            <StyledToolTipWrapper>
                {(disable || !canUserEdit) && <DisabledOverlay opacity={0} testId={disabledTestId} />}
                <InteractiveElement ref={ref} type="button" data-testid={testId} data-cy={testId} disabled={disable} className={className} onClick={onClick}>
                    <ChildrenContainer disabled={disable || !canUserEdit}>{children}</ChildrenContainer>
                    <span className="hidden">{toolTip}</span>
                </InteractiveElement>
            </StyledToolTipWrapper>
        </SimpleTooltip>
    )
);

type SidebarButtonWithTooltipTitleAndMessageProps = Pick<SidebarTopNavProps, "canUserEdit" | "disable" | "onClick" | "testId" | "className" | "disabledTestId"> & {
    tooltipTitle: string;
    tooltipMessage: string;
};

export const SidebarButtonWithTooltipTitleAndMessage = forwardRef<HTMLButtonElement, PropsWithChildren<SidebarButtonWithTooltipTitleAndMessageProps>>(
    ({ tooltipTitle, tooltipMessage, canUserEdit = true, disable = false, onClick, testId, disabledTestId, className, children }, ref) => (
        <TooltipTitleAndMessage title={tooltipTitle} message={tooltipMessage} placement="top">
            <StyledToolTipWrapper>
                {(disable || !canUserEdit) && <DisabledOverlay opacity={0} testId={disabledTestId} />}
                <InteractiveElement ref={ref} type="button" data-testid={testId} data-cy={testId} disabled={disable} className={className} onClick={onClick}>
                    <ChildrenContainer disabled={disable || !canUserEdit}>{children}</ChildrenContainer>
                    <span className="hidden">{tooltipTitle}</span>
                </InteractiveElement>
            </StyledToolTipWrapper>
        </TooltipTitleAndMessage>
    )
);

const ChildrenContainer = styled.div<{ disabled: boolean }>`
    opacity: ${({ disabled }) => `${disabled ? 0.5 : 1}`};
`;

const StyledToolTipWrapper = styled(ToolTipWrapper)`
    height: 100%;
`;
